/**
 * Single source of truth for every backend route. The .NET API mostly uses
 * `api/{controller}/{action}` with the action name in the path; three fee
 * controllers (`Term`, `HeadGroup`, `FeeStructure`) route by HTTP verb only,
 * so their path has no action segment.
 *
 * Nothing else in the app should contain a literal API path.
 */
export const endpoints = {
  /** The school dashboard in one read (Camptain.API/Controllers/Foundation/DashboardController). */
  dashboard: {
    summary: '/api/dashboard/summary',
  },
  /** File uploads (Camptain.API/Controllers/UploadsController). multipart/form-data, field `file`. */
  uploads: {
    images: '/api/uploads/images',
  },
  /**
   * Centralized document management (Camptain.API/Controllers/DocumentsController).
   * Module independent: a document is reached through `entityType`/`entityId`
   * (STUDENT/5021, ADMISSION/312 ...), never through a module's own route.
   */
  documents: {
    types: '/api/documents/types',
    upload: '/api/documents/upload',
    document: (documentId: number) => `/api/documents/${documentId}`,
    byEntity: (entityType: string, entityId: number) =>
      `/api/documents/entity/${encodeURIComponent(entityType)}/${entityId}`,
    versions: (documentId: number) => `/api/documents/${documentId}/versions`,
    download: (documentId: number) => `/api/documents/${documentId}/download`,
    links: (documentId: number) => `/api/documents/${documentId}/links`,
    activity: (documentId: number) => `/api/documents/${documentId}/activity`,
    permissions: (documentId: number) => `/api/documents/${documentId}/permissions`,
    /** Roles & Permissions screen (DocumentPermissionsController). */
    roles: '/api/documents/permissions/roles',
    permissionMatrix: '/api/documents/permissions/matrix',
    rolePermissions: (roleId: number) => `/api/documents/permissions/matrix/${roleId}`,
  },
  /**
   * Administration · Global Settings (GlobalSettingsController, KeyFormatsController).
   * `branch` false is the organization default layer; true is the branch the
   * request names in `X-Branch-Id` (see `branchScope`).
   */
  globalSettings: {
    settings: (branch: boolean) => (branch ? '/api/branch/global-settings' : '/api/global-settings'),
    keyFormats: (branch: boolean) => (branch ? '/api/branch/key-formats' : '/api/key-formats'),
    keyFormat: (branch: boolean, keyType: string) =>
      `${branch ? '/api/branch/key-formats' : '/api/key-formats'}/${keyType}`,
    keyFormatPreview: (branch: boolean, keyType: string) =>
      `${branch ? '/api/branch/key-formats' : '/api/key-formats'}/${keyType}/preview`,
  },
  /**
   * Administration · Global Settings · Letterhead (LetterheadsController).
   * `branch` false is the organization's templates; true is the templates of the
   * branch in `X-Branch-Id` (listed with the organization's). `resolve` acts for
   * the branch in `X-Branch-Id`.
   */
  letterheads: {
    list: (branch: boolean) => (branch ? '/api/branch/letterheads' : '/api/letterheads'),
    item: (branch: boolean, letterheadTemplateId: number) =>
      `${branch ? '/api/branch/letterheads' : '/api/letterheads'}/${letterheadTemplateId}`,
    nameExists: (branch: boolean) => `${branch ? '/api/branch/letterheads' : '/api/letterheads'}/name-exists`,
    resolve: '/api/branch/letterheads/resolve',
  },
  /**
   * The application-wide audit log (Camptain.API/Controllers/Foundation/AuditLogsController).
   * Read only; scoped to the caller's organization (and branch) by the request headers.
   */
  auditLogs: {
    list: '/api/audit-logs',
    log: (auditLogId: number) => `/api/audit-logs/${auditLogId}`,
    /** Records an export the browser wrote (Excel / PDF / CSV); 204. */
    exports: '/api/audit-logs/exports',
  },
  /**
   * Phase 1 Foundation API (Camptain.API/Controllers/Foundation). RESTful:
   * collections take GET (paged: `?page=&pageSize=`) and POST, items take GET,
   * POST and DELETE; a list read with more than two inputs is a POST to its
   * `.../search`. Every id is a business id (`school_id`, `class_id` ...).
   */
  foundation: {
    schools: '/api/schools',
    /** The school list (POST: more than two inputs); `schools` creates. */
    schoolSearch: '/api/schools/search',
    // Routes below without a schoolId act for the branch in X-Branch-Id
    // (api/schoolScope.ts): each branch has one school.
    // Routes below without a schoolId act for the branch in X-Branch-Id
    // (api/schoolScope.ts): each branch has one school.
    // Routes below without a schoolId act for the branch in X-Branch-Id
    // (api/schoolScope.ts): each branch has one school.
    /**
     * The common lookup API - every dropdown, select and autocomplete reads
     * from it; the CRUD routes above stay the source for full lists.
     */
    lookupTypes: () => `/api/lookups`,
    lookup: (type: string) => `/api/lookups/${type}`,
    school: (schoolId: number) => `/api/schools/${schoolId}`,
    schoolContacts: () => `/api/school-contacts`,
    schoolContact: (schoolContactId: number) =>
      `/api/school-contacts/${schoolContactId}`,
    academicYears: () => `/api/academic-years`,
    /** What the caller may do with past years (closed-year editing, rollover). */
    academicYearAccess: () => `/api/academic-years/access`,
    academicYear: (academicYearId: number) =>
      `/api/academic-years/${academicYearId}`,
    setCurrentAcademicYear: (academicYearId: number) =>
      `/api/academic-years/${academicYearId}/set-current`,
    /** Year rollover into the target year `toAcademicYearId` (POST runs the copy). */
    rollover: (toAcademicYearId: number) => `/api/academic-years/${toAcademicYearId}/rollover`,
    rolloverPreview: (toAcademicYearId: number) =>
      `/api/academic-years/${toAcademicYearId}/rollover/preview`,
    rolloverArrears: (toAcademicYearId: number) =>
      `/api/academic-years/${toAcademicYearId}/rollover/arrears`,
    rolloverFinish: (toAcademicYearId: number) =>
      `/api/academic-years/${toAcademicYearId}/rollover/finish`,
    educationBoards: () => `/api/education-boards`,
    educationBoard: (boardId: number) =>
      `/api/education-boards/${boardId}`,
    mediumsOfInstruction: () => `/api/mediums-of-instruction`,
    mediumOfInstruction: (mediumId: number) =>
      `/api/mediums-of-instruction/${mediumId}`,
    classes: () => `/api/classes`,
    class: (classId: number) => `/api/classes/${classId}`,
    sections: () => `/api/sections`,
    section: (sectionId: number) =>
      `/api/sections/${sectionId}`,
    subjects: () => `/api/subjects`,
    subject: (subjectId: number) =>
      `/api/subjects/${subjectId}`,
    houses: () => `/api/houses`,
    house: (houseId: number) => `/api/houses/${houseId}`,
    schoolClassSections: () => `/api/class-sections`,
    schoolClassSectionSubjects: () =>
      `/api/class-section-subjects`,
    classSections: (classId: number) => `/api/classes/${classId}/sections`,
    classSection: (classId: number, classSectionId: number) =>
      `/api/classes/${classId}/sections/${classSectionId}`,
    classSectionSubjects: (classSectionId: number) =>
      `/api/class-sections/${classSectionId}/subjects`,
    classSectionSubject: (classSectionId: number, classSectionSubjectId: number) =>
      `/api/class-sections/${classSectionId}/subjects/${classSectionSubjectId}`,
    gradeScales: () => `/api/grade-scales`,
    gradeScale: (gradeScaleId: number) =>
      `/api/grade-scales/${gradeScaleId}`,
    gradeScaleBands: (gradeScaleId: number) => `/api/grade-scales/${gradeScaleId}/bands`,
    gradeScaleBand: (gradeScaleId: number, gradeBandId: number) =>
      `/api/grade-scales/${gradeScaleId}/bands/${gradeBandId}`,
    classGradeScale: (classId: number) => `/api/classes/${classId}/grade-scale`,
    schoolClassGradeScales: () => `/api/class-grade-scales`,
    calendarEvents: () => `/api/calendar-events`,
    calendarEvent: (calendarEventId: number) =>
      `/api/calendar-events/${calendarEventId}`,
    calendarEventImport: () => `/api/calendar-events/import`,
    calendarEventImportValidate: () => `/api/calendar-events/import/validate`,
    lessonPlans: () => `/api/lesson-plans`,
    lessonPlan: (lessonPlanId: number) =>
      `/api/lesson-plans/${lessonPlanId}`,
    lessonPlanDelivery: (lessonPlanId: number) =>
      `/api/lesson-plans/${lessonPlanId}/delivery`,
    assignments: () => `/api/assignments`,
    assignment: (assignmentId: number) =>
      `/api/assignments/${assignmentId}`,
    /** A student's hand-in of an assignment, and its marking (AssignmentSubmissionsController). */
    assignmentSubmissions: (assignmentId: number) => `/api/assignments/${assignmentId}/submissions`,
    assignmentSubmissionFiles: (assignmentId: number, submissionId: number) =>
      `/api/assignments/${assignmentId}/submissions/${submissionId}/files`,
    assignmentSubmissionGrade: (assignmentId: number, submissionId: number) =>
      `/api/assignments/${assignmentId}/submissions/${submissionId}/grade`,
    homework: () => `/api/homework`,
    homeworkItem: (homeworkId: number) =>
      `/api/homework/${homeworkId}`,
    students: () => `/api/students`,
    studentImport: () => `/api/students/import`,
    studentImportValidate: () => `/api/students/import/validate`,
    student: (studentId: number) =>
      `/api/students/${studentId}`,
    studentAdmission: (studentId: number) =>
      `/api/students/${studentId}/admission`,
    studentAddresses: (studentId: number) =>
      `/api/students/${studentId}/addresses`,
    studentAddress: (studentId: number, studentAddressId: number) =>
      `/api/students/${studentId}/addresses/${studentAddressId}`,
    studentParents: (studentId: number) =>
      `/api/students/${studentId}/parents`,
    studentParent: (studentId: number, parentId: number) =>
      `/api/students/${studentId}/parents/${parentId}`,
    // One student's own records, for the student, their parents and staff (StudentRecordsController).
    studentLibrary: (studentId: number) =>
      `/api/students/${studentId}/library`,
    studentTransport: (studentId: number) =>
      `/api/students/${studentId}/transport`,
    studentHealth: (studentId: number) =>
      `/api/students/${studentId}/health`,
    studentCertificateRequests: (studentId: number) =>
      `/api/students/${studentId}/certificate-requests`,
    studentPreviousSchools: (studentId: number) =>
      `/api/students/${studentId}/previous-schools`,
    studentPreviousSchool: (studentId: number, previousSchoolId: number) =>
      `/api/students/${studentId}/previous-schools/${previousSchoolId}`,
    enrollments: () => `/api/enrollments`,
    /** Sections of a class with class teacher and free seats. */
    enrollmentAvailability: () =>
      `/api/enrollments/availability`,
    enrollment: (enrollmentId: number) =>
      `/api/enrollments/${enrollmentId}`,
    enrollmentSectionChange: (enrollmentId: number) =>
      `/api/enrollments/${enrollmentId}/section-change`,
    enrollmentSubjects: (enrollmentId: number) =>
      `/api/enrollments/${enrollmentId}/subjects`,
    promotions: () => `/api/promotions`,
    staff: () => `/api/staff`,
    staffMember: (staffId: number) => `/api/staff/${staffId}`,
    /** Staff member + qualifications + experiences, saved in one transaction. */
    staffProfiles: () => `/api/staff/profile`,
    /** One row of the bulk staff import: created ACTIVE at once (no activation checks). */
    staffImport: () => `/api/staff/import`,
    staffFieldPolicy: () => `/api/staff/field-policy`,
    /** The signed-in person's own profile, whatever their role (GET reads it, POST saves their own edits). */
    myProfile: () => `/api/me/profile`,
    staffAssignments: (staffId: number) =>
      `/api/staff/${staffId}/assignments`,
    staffAssignment: (staffId: number, assignmentId: number) =>
      `/api/staff/${staffId}/assignments/${assignmentId}`,
    staffAssignmentRollover: () => `/api/staff-assignments/rollover`,
    hostelBlocks: () => `/api/hostel-blocks`,
    transportRoutes: () => `/api/transport-routes`,
    /** activation-check, activate, leave, relieve, reactivate, status-history */
    staffLifecycle: (staffId: number, action: string) =>
      `/api/staff/${staffId}/${action}`,
    staffProfile: (staffId: number) =>
      `/api/staff/${staffId}/profile`,
    staffQualifications: (staffId: number) =>
      `/api/staff/${staffId}/qualifications`,
    staffQualification: (staffId: number, qualificationId: number) =>
      `/api/staff/${staffId}/qualifications/${qualificationId}`,
    staffExperiences: (staffId: number) =>
      `/api/staff/${staffId}/experiences`,
    staffExperience: (staffId: number, experienceId: number) =>
      `/api/staff/${staffId}/experiences/${experienceId}`,
    staffTypes: () => `/api/staff-types`,
    staffType: (staffTypeId: number) =>
      `/api/staff-types/${staffTypeId}`,
    staffTypeRoles: () => `/api/staff-types/roles`,
    studentCategories: () => `/api/student-categories`,
    studentCategorySearch: () => `/api/student-categories/search`,
    studentCategory: (studentCategoryId: number) =>
      `/api/student-categories/${studentCategoryId}`,
    studentCategoryAssignments: (studentId: number) =>
      `/api/students/${studentId}/category-assignments`,
    studentCategoryAssignmentSearch: (studentId: number) =>
      `/api/students/${studentId}/category-assignments/search`,
    studentCategoryAssignment: (studentId: number, assignmentId: number) =>
      `/api/students/${studentId}/category-assignments/${assignmentId}`,
    studentDisciplineRecords: (studentId: number) =>
      `/api/students/${studentId}/discipline-records`,
    studentDisciplineRecordSearch: (studentId: number) =>
      `/api/students/${studentId}/discipline-records/search`,
    studentDisciplineRecord: (studentId: number, disciplineRecordId: number) =>
      `/api/students/${studentId}/discipline-records/${disciplineRecordId}`,
    promotionPolicies: () => `/api/promotion-policies`,
    promotionPolicySearch: () => `/api/promotion-policies/search`,
    promotionPolicy: (policyId: number) => `/api/promotion-policies/${policyId}`,
    promotionPolicyDuplicate: (policyId: number) => `/api/promotion-policies/${policyId}/duplicate`,
    promotionPolicyActivate: (policyId: number) => `/api/promotion-policies/${policyId}/activate`,
    promotionPolicyDeactivate: (policyId: number) => `/api/promotion-policies/${policyId}/deactivate`,
    promotionRules: (policyId: number) => `/api/promotion-policies/${policyId}/rules`,
    promotionRuleSearch: (policyId: number) => `/api/promotion-policies/${policyId}/rules/search`,
    promotionRule: (policyId: number, ruleId: number) => `/api/promotion-policies/${policyId}/rules/${ruleId}`,
    promotionRuleActivate: (policyId: number, ruleId: number) =>
      `/api/promotion-policies/${policyId}/rules/${ruleId}/activate`,
    promotionRuleDeactivate: (policyId: number, ruleId: number) =>
      `/api/promotion-policies/${policyId}/rules/${ruleId}/deactivate`,
    promotionPolicyEvaluate: (policyId: number) => `/api/promotion-policies/${policyId}/evaluate`,
    promotionShuffleSettings: (policyId: number) => `/api/promotion-policies/${policyId}/shuffle-settings`,
    promotionRuleFields: () => `/api/promotion-rules/fields`,
    promotionRuleValidate: () => `/api/promotion-rules/validate`,
    designations: () => `/api/designations`,
    designation: (designationId: number) =>
      `/api/designations/${designationId}`,
    departments: () => `/api/departments`,
    department: (departmentId: number) =>
      `/api/departments/${departmentId}`,
    periods: () => `/api/periods`,
    period: (periodId: number) => `/api/periods/${periodId}`,
    rooms: () => `/api/rooms`,
    room: (roomId: number) => `/api/rooms/${roomId}`,
    roomEquipment: (roomId: number) => `/api/rooms/${roomId}/equipment`,
    roomAvailability: (roomId: number) => `/api/rooms/${roomId}/availability`,
    roomExceptions: (roomId: number) => `/api/rooms/${roomId}/exceptions`,
    teacherAvailability: () => `/api/teacher-availability`,
    teacherAvailabilityOf: (staffId: number) => `/api/teacher-availability/${staffId}`,
    copyTeacherAvailability: (staffId: number) => `/api/teacher-availability/${staffId}/copy`,
    roomException: (roomId: number, roomExceptionId: number) =>
      `/api/rooms/${roomId}/exceptions/${roomExceptionId}`,
    bellSchedules: () => `/api/bell-schedules`,
    bellSchedule: (bellScheduleId: number) => `/api/bell-schedules/${bellScheduleId}`,
    copyBellSchedule: (bellScheduleId: number) => `/api/bell-schedules/${bellScheduleId}/copy`,
    timetableSettings: () => `/api/timetable-settings`,
    /** Timetable Overview: setup steps, checks, versions and headline numbers of a year. */
    timetableOverview: () => `/api/timetable-overview`,
    /** Generate Timetable: readiness, versions (runs), publishing. */
    timetableReadiness: () => `/api/timetable-generation/readiness`,
    timetableRuns: () => `/api/timetable-generation/runs`,
    timetableRun: (runId: number) => `/api/timetable-generation/runs/${runId}`,
    timetableRunName: (runId: number) => `/api/timetable-generation/runs/${runId}/name`,
    publishTimetableRun: (runId: number) => `/api/timetable-generation/runs/${runId}/publish`,
    publishedTimetable: () => `/api/timetable-generation/published`,
    /** Timetables: read-only class and teacher views of a version. */
    timetableViewOptions: () => `/api/timetables/options`,
    classTimetable: () => `/api/timetables/class`,
    teacherTimetable: () => `/api/timetables/teacher`,
    /** My Timetable: the signed-in student's, child's or teacher's own week (published only). */
    myTimetableOptions: () => `/api/timetables/mine/options`,
    myClassTimetable: () => `/api/timetables/mine/class`,
    myTeacherTimetable: () => `/api/timetables/mine/teacher`,
    /** Timetable Editor: hand edits to a draft version. */
    timetableLessonOptions: (runId: number, activityId: number, lessonIndex: number) =>
      `/api/timetable-generation/runs/${runId}/lessons/${activityId}/${lessonIndex}/options`,
    timetableLessonEdit: (runId: number, edit: 'move' | 'room' | 'unplace' | 'lock') =>
      `/api/timetable-generation/runs/${runId}/lessons/${edit}`,
    timetableSlotChoices: (runId: number) => `/api/timetable-generation/runs/${runId}/slots`,
    copyTimetableRun: (runId: number) => `/api/timetable-generation/runs/${runId}/copy`,
    /** Substitutions: absent teachers and how their lessons are covered on a day. */
    substitutions: () => `/api/substitutions`,
    substitutionAbsences: () => `/api/substitutions/absences`,
    substitutionAbsence: (teacherAbsenceId: number) => `/api/substitutions/absences/${teacherAbsenceId}`,
    substitutionCandidates: () => `/api/substitutions/candidates`,
    substitutionAssign: () => `/api/substitutions/assign`,
    substitution: (substitutionId: number) => `/api/substitutions/${substitutionId}`,
    substitutionAutoAssign: () => `/api/substitutions/auto-assign`,
    substitutionNotify: () => `/api/substitutions/notify`,
    /** Student Groups: subject-split groups of a class section. */
    studentGroups: () => `/api/student-groups`,
    studentGroup: (studentGroupId: number) => `/api/student-groups/${studentGroupId}`,
    studentGroupStatus: (studentGroupId: number) => `/api/student-groups/${studentGroupId}/status`,
  },

  students: {
    list: '/api/Student/GetStudentDetail',
    insert: '/api/Student/InsertStudentDetail',
    remove: '/api/Student/DeleteStudentDetail',
    previousSchool: '/api/Student/GetPreviousSchoolDetail',
    insertPreviousSchool: '/api/Student/InsertPreviousSchoolDetail',
    deletePreviousSchool: '/api/Student/DeletePreviousSchoolDetail',
    contacts: '/api/Student/GetStudentContactDetail',
    insertContact: '/api/Student/InsertStudentContactDetail',
    deleteContact: '/api/Student/DeleteStudentContactDetail',
    parentInfo: '/api/Student/GetParentInfo',
    insertParentInfo: '/api/Student/InsertParentInfo',
    deleteParentInfo: '/api/Student/DeleteParentInfo',
  },
  staff: {
    listAll: '/api/Staff/GetAllStaffDetail',
    insertPersonal: '/api/Staff/InsertStaffDetail',
    insertAll: '/api/Staff/InsertAllStaffDetails',
    deleteAll: '/api/Staff/DeleteAllStaffDetail',
    education: '/api/Staff/GetEducationDetail',
    insertEducation: '/api/Staff/InsertEducationDetail',
    deleteEducation: '/api/Staff/DeleteEducationDetail',
    experience: '/api/Staff/GetExperienceDetail',
    insertExperience: '/api/Staff/InsertExperienceDetail',
    deleteExperience: '/api/Staff/DeleteExperienceDetail',
    contact: '/api/Staff/GetContactDetail',
    insertContact: '/api/Staff/InsertContactDetail',
    deleteContact: '/api/Staff/DeleteContactDetail',
  },
  transport: {
    routes: '/api/Demo/GetRouteDetail',
    saveRoute: '/api/Demo/SaveRouteDetail',
    deleteRoute: '/api/Demo/DeleteRouteDetail',
    routeNames: '/api/Transport/GetRouteNames',
    stops: '/api/Demo/GetStopDetail',
    manageStop: '/api/Demo/ManageStopDetail',
    deleteStop: '/api/Demo/DeleteStopDetail',
    stopStudents: '/api/Demo/GetStudentDetail',
    vehicles: '/api/Vechicle/GetVehicleDetail',
    createVehicle: '/api/Vechicle/CreateVehicleDetail',
    deleteVehicle: '/api/Vechicle/DeleteVehicleDetail',
    vehicleStudents: '/api/Vechicle/GetStudentDetail',
    expenses: '/api/Vechicle/GetExpenseDetail',
    createExpense: '/api/Vechicle/CreateExpenseDetail',
    deleteExpense: '/api/Vechicle/DeleteExpenseDetail',
    drivers: '/api/Transport/GetDrivers',
    saveDriver: '/api/Transport/SaveDriver',
    deleteDriver: '/api/Transport/DeleteDriver',
    allocations: '/api/Transport/GetAllocations',
    saveAllocation: '/api/Transport/SaveAllocation',
    deleteAllocation: '/api/Transport/DeleteAllocation',
    attendance: '/api/Transport/GetAttendance',
    saveAttendance: '/api/Transport/SaveAttendance',
    deleteAttendance: '/api/Transport/DeleteAttendance',
    tripLogs: '/api/Transport/GetTripLogs',
    saveTripLog: '/api/Transport/SaveTripLog',
    deleteTripLog: '/api/Transport/DeleteTripLog',
    transportFees: '/api/Transport/GetFees',
    saveTransportFee: '/api/Transport/SaveFee',
    deleteTransportFee: '/api/Transport/DeleteFee',
    maintenance: '/api/Transport/GetMaintenance',
    saveMaintenance: '/api/Transport/SaveMaintenance',
    deleteMaintenance: '/api/Transport/DeleteMaintenance',
    alerts: '/api/Transport/GetAlerts',
    saveAlert: '/api/Transport/SaveAlert',
    deleteAlert: '/api/Transport/DeleteAlert',
  },
  fees: {
    heads: '/api/FeeStructure',
    insertHead: '/api/FeeStructure',
    deleteHead: '/api/FeeStructure',
    headTypes: '/api/HeadGroup',
    insertHeadType: '/api/HeadGroup',
    deleteHeadType: '/api/HeadGroup',
    terms: '/api/Term',
    insertTerm: '/api/Term',
    deleteTerm: '/api/Term',
    structures: '/api/FeeStructureHeader',
    saveStructure: '/api/FeeStructureHeader',
    deleteStructure: '/api/FeeStructureHeader',
    structureItems: '/api/FeeStructureItem',
    saveStructureItem: '/api/FeeStructureItem',
    deleteStructureItem: '/api/FeeStructureItem',
    structureAdmissionItems: '/api/FeeStructureItem/Admission',
    saveStructureAdmissionMapping: '/api/FeeStructureItem/SaveAdmissionMapping',
    payments: '/api/fee-receipts',
    savePayment: '/api/fee-receipts',
    deletePayment: '/api/fee-receipts',
    adjustments: '/api/StudentFeeAdjustment/Search',
    saveAdjustment: '/api/StudentFeeAdjustment',
    deleteAdjustment: '/api/StudentFeeAdjustment',
    studentFees: '/api/student-fees',
    generateStudentFees: '/api/student-fees/generate',
    invoices: '/api/fee-invoices',
    scholarships: '/api/Finance/GetScholarships',
    saveScholarship: '/api/Finance/SaveScholarship',
    deleteScholarship: '/api/Finance/DeleteScholarship',
    refunds: '/api/fee-refunds',
    saveRefund: '/api/fee-refunds',
    deleteRefund: '/api/fee-refunds',
    expenses: '/api/Finance/GetExpenses',
    saveExpense: '/api/Finance/SaveExpense',
    deleteExpense: '/api/Finance/DeleteExpense',
    budgets: '/api/Finance/GetBudgets',
    saveBudget: '/api/Finance/SaveBudget',
    deleteBudget: '/api/Finance/DeleteBudget',
    savedReports: '/api/FeeReports/Saved',
    savedReportNameExists: '/api/FeeReports/Saved/NameExists',
    shareSavedReport: '/api/FeeReports/Saved/Share',
    runSavedReport: '/api/FeeReports/Saved/Run',
    paymentSettings: '/api/Finance/GetPaymentSettings',
    savePaymentSettings: '/api/Finance/SavePaymentSettings',
    dashboardSummary: '/api/Finance/GetDashboardSummary',
    paymentAccounts: '/api/Finance/GetPaymentAccounts',
    savePaymentAccount: '/api/Finance/SavePaymentAccount',
    deletePaymentAccount: '/api/Finance/DeletePaymentAccount',
  },
  enrollment: {
    list: '/api/Enrollment/GetEnrollment',
    insert: '/api/Enrollment/InsertEnrollment',
    delete: '/api/Enrollment/DeleteEnrollment',
  },
  assessment: {
    list: '/api/Assessment/GetAssessment',
    insert: '/api/Assessment/InsertAssessment',
    delete: '/api/Assessment/DeleteAssessment',
    roster: '/api/Assessment/GetMarksRoster',
    saveMarks: '/api/Assessment/SaveMarks',
  },
  examination: {
    exams: '/api/Examination/GetExams',
    insertExam: '/api/Examination/InsertExam',
    deleteExam: '/api/Examination/DeleteExam',
    subjects: '/api/Examination/GetSubjects',
    insertSubject: '/api/Examination/InsertSubject',
    deleteSubject: '/api/Examination/DeleteSubject',
    subjectMatrix: '/api/Examination/GetSubjectMatrix',
    saveSubjectMatrix: '/api/Examination/SaveSubjectMatrix',
    schedule: '/api/Examination/GetSchedule',
    saveSchedule: '/api/Examination/SaveSchedule',
    saveScheduleBatch: '/api/Examination/SaveScheduleBatch',
    deleteSchedule: '/api/Examination/DeleteSchedule',
    hallTicketEligibility: '/api/Examination/GetHallTicketEligibility',
    generateHallTickets: '/api/Examination/GenerateHallTickets',
    hallTickets: '/api/Examination/GetHallTickets',
    revokeHallTickets: '/api/Examination/RevokeHallTickets',
    publishHallTickets: '/api/Examination/PublishHallTickets',
    marksSheet: '/api/Examination/GetMarksSheet',
    saveMarks: '/api/Examination/SaveMarks',
    facultyMarkPapers: '/api/Examination/GetFacultyMarkPapers',
    facultyMarksSheet: '/api/Examination/GetFacultyMarksSheet',
    saveFacultyMarks: '/api/Examination/SaveFacultyMarks',
    results: '/api/Examination/GetResults',
    publishStatus: '/api/Examination/GetPublishStatus',
    publish: '/api/Examination/PublishResults',
    schemes: '/api/Examination/GetSchemes',
    scheme: '/api/Examination/GetScheme',
    saveScheme: '/api/Examination/SaveScheme',
    deleteScheme: '/api/Examination/DeleteScheme',
    saveExamTerm: '/api/Examination/SaveExamTerm',
    deleteExamTerm: '/api/Examination/DeleteExamTerm',
    saveSchemeExam: '/api/Examination/SaveSchemeExam',
    schemeResults: '/api/Examination/GetSchemeResults',
    schemeRanks: '/api/Examination/GetSchemeRanks',
    deleteSchemeExam: '/api/Examination/DeleteSchemeExam',
    saveSchemeSections: '/api/Examination/SaveSchemeSections',
    schemeSubjects: '/api/Examination/GetSchemeSubjects',
    clearSchemeSectionSubjects: '/api/Examination/ClearSchemeSectionSubjects',
    saveSchemeSubjects: '/api/Examination/SaveSchemeSubjects',
    validateScheme: '/api/Examination/ValidateScheme',
    publishScheme: '/api/Examination/PublishScheme',
    schemeTemplates: '/api/Examination/GetSchemeTemplates',
    createSchemeFromTemplate: '/api/Examination/CreateSchemeFromTemplate',
    copyScheme: '/api/Examination/CopyScheme',
    examTypes: '/api/Examination/GetExamTypes',
    saveExamType: '/api/Examination/SaveExamType',
    deleteExamType: '/api/Examination/DeleteExamType',
    questionPapers: '/api/Examination/GetQuestionPapers',
    saveQuestionPaper: '/api/Examination/SaveQuestionPaper',
    deleteQuestionPaper: '/api/Examination/DeleteQuestionPaper',
    examAttendance: '/api/Examination/GetExamAttendance',
    saveExamAttendance: '/api/Examination/SaveExamAttendance',
    deleteExamAttendance: '/api/Examination/DeleteExamAttendance',
    attendanceSheet: '/api/Examination/GetAttendanceSheet',
    saveAttendanceSheet: '/api/Examination/SaveAttendanceSheet',
    gradingAssignments: '/api/Examination/GetGradingAssignments',
    saveGradingAssignment: '/api/Examination/SaveGradingAssignment',
    deleteGradingAssignment: '/api/Examination/DeleteGradingAssignment',
    reportCardRemarks: '/api/Examination/GetReportCardRemarks',
    saveReportCardRemark: '/api/Examination/SaveReportCardRemark',
    saveReportCardRemarksBulk: '/api/Examination/SaveReportCardRemarksBulk',
    deleteReportCardRemark: '/api/Examination/DeleteReportCardRemark',
    transcriptRequests: '/api/Examination/GetTranscriptRequests',
    saveTranscriptRequest: '/api/Examination/SaveTranscriptRequest',
    deleteTranscriptRequest: '/api/Examination/DeleteTranscriptRequest',
    transcript: '/api/Examination/GetTranscript',
    reportSummary: '/api/Examination/GetExamReportSummary',
    revaluationMark: '/api/Examination/GetRevaluationMark',
    revaluations: '/api/Examination/GetRevaluations',
    saveRevaluation: '/api/Examination/SaveRevaluation',
    deleteRevaluation: '/api/Examination/DeleteRevaluation',
  },
  attendance: {
    settings: '/api/Attendance/GetSettings',
    saveSettings: '/api/Attendance/SaveSettings',
    mark: '/api/Attendance/MarkAttendance',
    matrix: '/api/Attendance/GetMatrix',
    /** One student's own marks: the read a student or parent account may make (the class-wide ones are staff only). */
    studentAttendance: '/api/Attendance/GetStudentAttendance',
    summary: '/api/Attendance/GetSummary',
    /** One page of the register's marks / of the report (search, filters, sorting and paging in the API). */
    matrixPage: '/api/Attendance/GetMatrixPage',
    summaryPage: '/api/Attendance/GetSummaryPage',
  },
  staffAttendance: {
    matrix: '/api/StaffAttendance/GetMatrix',
    mark: '/api/StaffAttendance/MarkAttendance',
  },
  homework: {
    list: '/api/Homework/GetHomework',
    insert: '/api/Homework/InsertHomework',
    delete: '/api/Homework/DeleteHomework',
  },
  timetable: {
    periods: '/api/Timetable/GetPeriods',
    savePeriod: '/api/Timetable/SavePeriod',
    deletePeriod: '/api/Timetable/DeletePeriod',
    entries: '/api/Timetable/GetEntries',
    saveEntry: '/api/Timetable/SaveEntry',
    deleteEntry: '/api/Timetable/DeleteEntry',
    clearGenerated: '/api/Timetable/ClearGenerated',
    rooms: '/api/Timetable/GetRooms',
    saveRoom: '/api/Timetable/SaveRoom',
    deleteRoom: '/api/Timetable/DeleteRoom',
    activities: '/api/Timetable/GetActivities',
    saveActivity: '/api/Timetable/SaveActivity',
    deleteActivity: '/api/Timetable/DeleteActivity',
  },
  library: {
    categories: '/api/Library/GetCategories',
    saveCategory: '/api/Library/SaveCategory',
    deleteCategory: '/api/Library/DeleteCategory',
    contributors: '/api/Library/GetContributors',
    saveContributor: '/api/Library/SaveContributor',
    deleteContributor: '/api/Library/DeleteContributor',
    books: '/api/Library/GetBooks',
    saveBook: '/api/Library/SaveBook',
    deleteBook: '/api/Library/DeleteBook',
    members: '/api/Library/GetMembers',
    saveMember: '/api/Library/SaveMember',
    deleteMember: '/api/Library/DeleteMember',
    circulation: '/api/Library/GetCirculation',
    issue: '/api/Library/IssueBook',
    returnBook: '/api/Library/ReturnBook',
    deleteCirculation: '/api/Library/DeleteCirculation',
    reservations: '/api/Library/GetReservations',
    saveReservation: '/api/Library/SaveReservation',
    deleteReservation: '/api/Library/DeleteReservation',
    fines: '/api/Library/GetFines',
    updateFine: '/api/Library/UpdateFine',
    stockAdjustments: '/api/Library/GetStockAdjustments',
    saveStockAdjustment: '/api/Library/SaveStockAdjustment',
    deleteStockAdjustment: '/api/Library/DeleteStockAdjustment',
  },
  hostel: {
    blocks: '/api/Hostel/GetBlocks',
    saveBlock: '/api/Hostel/SaveBlock',
    deleteBlock: '/api/Hostel/DeleteBlock',
    rooms: '/api/Hostel/GetRooms',
    saveRoom: '/api/Hostel/SaveRoom',
    deleteRoom: '/api/Hostel/DeleteRoom',
    beds: '/api/Hostel/GetBeds',
    saveBed: '/api/Hostel/SaveBed',
    deleteBed: '/api/Hostel/DeleteBed',
    allocations: '/api/Hostel/GetAllocations',
    saveAllocation: '/api/Hostel/SaveAllocation',
    deleteAllocation: '/api/Hostel/DeleteAllocation',
    attendance: '/api/Hostel/GetAttendance',
    saveAttendance: '/api/Hostel/SaveAttendance',
    deleteAttendance: '/api/Hostel/DeleteAttendance',
    mealMenus: '/api/Hostel/GetMealMenus',
    saveMealMenu: '/api/Hostel/SaveMealMenu',
    deleteMealMenu: '/api/Hostel/DeleteMealMenu',
    staff: '/api/Hostel/GetStaff',
    saveStaff: '/api/Hostel/SaveStaff',
    deleteStaff: '/api/Hostel/DeleteStaff',
    visitors: '/api/Hostel/GetVisitors',
    saveVisitor: '/api/Hostel/SaveVisitor',
    deleteVisitor: '/api/Hostel/DeleteVisitor',
    complaints: '/api/Hostel/GetComplaints',
    saveComplaint: '/api/Hostel/SaveComplaint',
    deleteComplaint: '/api/Hostel/DeleteComplaint',
    maintenance: '/api/Hostel/GetMaintenance',
    saveMaintenance: '/api/Hostel/SaveMaintenance',
    deleteMaintenance: '/api/Hostel/DeleteMaintenance',
  },
  admissions: {
    /** One page of each list, for the tables (search, filters, sorting and paging run in the API). */
    pages: {
      enquiries: '/api/Admissions/GetEnquiriesPage',
      applicants: '/api/Admissions/GetApplicantsPage',
      unscheduledApplicants: '/api/Admissions/GetUnscheduledApplicantsPage',
      documents: '/api/Admissions/GetDocumentsPage',
      tests: '/api/Admissions/GetEntranceTestsPage',
      interviews: '/api/Admissions/GetInterviewsPage',
      counselling: '/api/Admissions/GetCounsellingPage',
      selections: '/api/Admissions/GetSelectionsPage',
      approvals: '/api/Admissions/GetApprovalsPage',
      waitingList: '/api/Admissions/GetWaitingListPage',
      interactions: '/api/Admissions/GetInteractionsPage',
    },
    enquiries: '/api/Admissions/GetEnquiries',
    saveEnquiry: '/api/Admissions/SaveEnquiry',
    deleteEnquiry: '/api/Admissions/DeleteEnquiry',
    enrolStudent: '/api/Admissions/EnrolStudent',
    applicants: '/api/Admissions/GetApplicants',
    applicant: '/api/Admissions/GetApplicant',
    saveApplicant: '/api/Admissions/SaveApplicant',
    submitApplication: '/api/Admissions/SubmitApplication',
    deleteApplicant: '/api/Admissions/DeleteApplicant',
    verifyApplication: '/api/Admissions/VerifyApplication',
    stageEligibleApplicants: '/api/Admissions/GetStageEligibleApplicants',
    setApplicantStages: '/api/Admissions/SetApplicantStages',
    waitlistApplicant: '/api/Admissions/WaitlistApplicant',
    rejectApplicant: '/api/Admissions/RejectApplicant',
    documents: '/api/Admissions/GetDocuments',
    saveDocument: '/api/Admissions/SaveDocument',
    deleteDocument: '/api/Admissions/DeleteDocument',
    /** multipart/form-data: ApplicantId, DocumentType (checklist name) and `file`. */
    uploadDocument: '/api/Admissions/UploadDocument',
    removeDocumentFile: '/api/Admissions/RemoveDocumentFile',
    verifyDocument: '/api/Admissions/VerifyDocument',
    tests: '/api/Admissions/GetEntranceTests',
    saveTest: '/api/Admissions/SaveEntranceTest',
    deleteTest: '/api/Admissions/DeleteEntranceTest',
    interviews: '/api/Admissions/GetInterviews',
    saveInterview: '/api/Admissions/SaveInterview',
    deleteInterview: '/api/Admissions/DeleteInterview',
    counselling: '/api/Admissions/GetCounselling',
    saveCounselling: '/api/Admissions/SaveCounselling',
    deleteCounselling: '/api/Admissions/DeleteCounselling',
    selections: '/api/Admissions/GetSelections',
    saveSelection: '/api/Admissions/SaveSelection',
    deleteSelection: '/api/Admissions/DeleteSelection',
    approvals: '/api/Admissions/GetApprovals',
    saveApproval: '/api/Admissions/SaveApproval',
    deleteApproval: '/api/Admissions/DeleteApproval',
    feePayments: '/api/Admissions/GetFeePayments',
    admissionFees: '/api/Admissions/GetAdmissionFees',
    admissionFee: '/api/Admissions/GetAdmissionFee',
    collectAdmissionFee: '/api/Admissions/CollectAdmissionFee',
    refundAdmissionFee: '/api/Admissions/RefundAdmissionFee',
    cancelAdmissionFeePayment: '/api/Admissions/CancelAdmissionFeePayment',
    seatAvailability: '/api/Admissions/GetSeatAvailability',
    scheduleEntranceTests: '/api/Admissions/ScheduleEntranceTests',
    scheduleInterviews: '/api/Admissions/ScheduleInterviews',
    scheduleCounselling: '/api/Admissions/ScheduleCounselling',
    saveSelections: '/api/Admissions/SaveSelections',
    saveApprovals: '/api/Admissions/SaveApprovals',
    updateEntranceTests: '/api/Admissions/UpdateEntranceTests',
    updateInterviews: '/api/Admissions/UpdateInterviews',
    updateSelections: '/api/Admissions/UpdateSelections',
    updateApprovals: '/api/Admissions/UpdateApprovals',
    moveApplicantStages: '/api/Admissions/MoveApplicantStages',
    waitingList: '/api/Admissions/GetWaitingList',
    saveWaitingList: '/api/Admissions/SaveWaitingList',
    deleteWaitingList: '/api/Admissions/DeleteWaitingList',
    interactionSummary: '/api/Admissions/GetInteractionSummary',
    interactionTimeline: '/api/Admissions/GetApplicantInteractionTimeline',
    scheduleInteraction: '/api/Admissions/ScheduleInteraction',
    saveInteractionSchedule: '/api/Admissions/SaveInteractionSchedule',
    updateInteraction: '/api/Admissions/UpdateInteraction',
    updateInteractionsStatus: '/api/Admissions/UpdateInteractionsStatus',
  },
  portal: {
    requestOtp: '/api/Public/Portal/RequestOtp',
    verifyOtp: '/api/Public/Portal/VerifyOtp',
    myApplications: '/api/Public/Portal/MyApplications',
  },
  health: {
    records: '/api/Health/GetRecords',
    saveRecord: '/api/Health/SaveRecord',
    deleteRecord: '/api/Health/DeleteRecord',
    incidents: '/api/Health/GetIncidents',
    saveIncident: '/api/Health/SaveIncident',
    deleteIncident: '/api/Health/DeleteIncident',
  },
  sports: {
    sports: '/api/Sports/GetSports',
    saveSport: '/api/Sports/SaveSport',
    deleteSport: '/api/Sports/DeleteSport',
    teams: '/api/Sports/GetTeams',
    saveTeam: '/api/Sports/SaveTeam',
    deleteTeam: '/api/Sports/DeleteTeam',
    coaches: '/api/Sports/GetCoaches',
    saveCoach: '/api/Sports/SaveCoach',
    deleteCoach: '/api/Sports/DeleteCoach',
    players: '/api/Sports/GetPlayers',
    savePlayer: '/api/Sports/SavePlayer',
    deletePlayer: '/api/Sports/DeletePlayer',
    trainingSchedules: '/api/Sports/GetTrainingSchedules',
    saveTrainingSchedule: '/api/Sports/SaveTrainingSchedule',
    deleteTrainingSchedule: '/api/Sports/DeleteTrainingSchedule',
    matches: '/api/Sports/GetMatches',
    saveMatch: '/api/Sports/SaveMatch',
    deleteMatch: '/api/Sports/DeleteMatch',
    attendance: '/api/Sports/GetAttendance',
    saveAttendance: '/api/Sports/SaveAttendance',
    deleteAttendance: '/api/Sports/DeleteAttendance',
    performance: '/api/Sports/GetPerformance',
    savePerformance: '/api/Sports/SavePerformance',
    deletePerformance: '/api/Sports/DeletePerformance',
    tournaments: '/api/Sports/GetTournaments',
    saveTournament: '/api/Sports/SaveTournament',
    deleteTournament: '/api/Sports/DeleteTournament',
    achievements: '/api/Sports/GetAchievements',
    saveAchievement: '/api/Sports/SaveAchievement',
    deleteAchievement: '/api/Sports/DeleteAchievement',
    facilities: '/api/Sports/GetFacilities',
    saveFacility: '/api/Sports/SaveFacility',
    deleteFacility: '/api/Sports/DeleteFacility',
  },
  facility: {
    buildings: '/api/Facility/GetBuildings',
    saveBuilding: '/api/Facility/SaveBuilding',
    deleteBuilding: '/api/Facility/DeleteBuilding',
    facilities: '/api/Facility/GetFacilities',
    saveFacility: '/api/Facility/SaveFacility',
    deleteFacility: '/api/Facility/DeleteFacility',
    bookings: '/api/Facility/GetBookings',
    saveBooking: '/api/Facility/SaveBooking',
    deleteBooking: '/api/Facility/DeleteBooking',
    maintenance: '/api/Facility/GetMaintenance',
    saveMaintenance: '/api/Facility/SaveMaintenance',
    deleteMaintenance: '/api/Facility/DeleteMaintenance',
    cleaning: '/api/Facility/GetCleaning',
    saveCleaning: '/api/Facility/SaveCleaning',
    deleteCleaning: '/api/Facility/DeleteCleaning',
    security: '/api/Facility/GetSecurity',
    saveSecurity: '/api/Facility/SaveSecurity',
    deleteSecurity: '/api/Facility/DeleteSecurity',
  },
  communication: {
    announcements: '/api/Communication/GetAnnouncements',
    saveAnnouncement: '/api/Communication/SaveAnnouncement',
    deleteAnnouncement: '/api/Communication/DeleteAnnouncement',
    notices: '/api/Communication/GetNotices',
    saveNotice: '/api/Communication/SaveNotice',
    deleteNotice: '/api/Communication/DeleteNotice',
    outboundMessages: '/api/Communication/GetOutboundMessages',
    saveOutboundMessage: '/api/Communication/SaveOutboundMessage',
    deleteOutboundMessage: '/api/Communication/DeleteOutboundMessage',
    directMessages: '/api/Communication/GetDirectMessages',
    saveDirectMessage: '/api/Communication/SaveDirectMessage',
    deleteDirectMessage: '/api/Communication/DeleteDirectMessage',
    events: '/api/Communication/GetEvents',
    saveEvent: '/api/Communication/SaveEvent',
    deleteEvent: '/api/Communication/DeleteEvent',
    feedback: '/api/Communication/GetFeedback',
    saveFeedback: '/api/Communication/SaveFeedback',
    deleteFeedback: '/api/Communication/DeleteFeedback',
    emergencyAlerts: '/api/Communication/GetEmergencyAlerts',
    saveEmergencyAlert: '/api/Communication/SaveEmergencyAlert',
    deleteEmergencyAlert: '/api/Communication/DeleteEmergencyAlert',
    templates: '/api/Communication/GetTemplates',
    saveTemplate: '/api/Communication/SaveTemplate',
    deleteTemplate: '/api/Communication/DeleteTemplate',
  },
  facultyPortal: {
    lessonPlans: '/api/FacultyPortal/GetLessonPlans',
    saveLessonPlan: '/api/FacultyPortal/SaveLessonPlan',
    deleteLessonPlan: '/api/FacultyPortal/DeleteLessonPlan',
    assignments: '/api/FacultyPortal/GetAssignments',
    saveAssignment: '/api/FacultyPortal/SaveAssignment',
    deleteAssignment: '/api/FacultyPortal/DeleteAssignment',
    leaveRequests: '/api/FacultyPortal/GetLeaveRequests',
    saveLeaveRequest: '/api/FacultyPortal/SaveLeaveRequest',
    deleteLeaveRequest: '/api/FacultyPortal/DeleteLeaveRequest',
  },
  parentPortal: {
    leaveRequests: '/api/ParentPortal/GetLeaveRequests',
    saveLeaveRequest: '/api/ParentPortal/SaveLeaveRequest',
    deleteLeaveRequest: '/api/ParentPortal/DeleteLeaveRequest',
  },
  principalPortal: {
    disciplineRecords: '/api/PrincipalPortal/GetDisciplineRecords',
    saveDisciplineRecord: '/api/PrincipalPortal/SaveDisciplineRecord',
    deleteDisciplineRecord: '/api/PrincipalPortal/DeleteDisciplineRecord',
    parentIssues: '/api/PrincipalPortal/GetParentIssues',
    saveParentIssue: '/api/PrincipalPortal/SaveParentIssue',
    deleteParentIssue: '/api/PrincipalPortal/DeleteParentIssue',
  },
} as const;
